import {
    ChangeDetectionStrategy,
    Component,
    inject,
    model,
    OnInit,
    ViewEncapsulation,
    input,
    signal,
    output,
} from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ICheckpointGroup, ICheckpointV2 } from '@fi-shared/checklist/checklist-v2';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { isDefined } from '@fi-shared/utils';
import { TResultPromiseData } from '@fi-shared/fi-shared-interfaces';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { FiDialogConfirmDeleteComponent } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete.component';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { isEqual } from '@fi-shared/utils/is-equal';

@Component({
    selector: 'fi-checkpoint-group',
    templateUrl: './checkpoint-group.component.html',
    styleUrls: ['./checkpoint-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-group' },
    standalone: false,
})
export class FiCheckpointGroupComponent implements OnInit {
    private appTextService = inject(AppTextService);
    private fiChecklistHelperService = inject(FiChecklistHelperService);
    private fiCheckpoint2HelperService = inject(FiCheckpoint2HelperService);
    private fiChecklistStoreService = inject(FiChecklistStoreService);
    private fiChecklist2Service = inject(FiChecklist2Service);
    private fiDialogsService = inject(FiDialogsService);

    checkpointGroups = model<ICheckpointGroup[]>([]);
    isComplianceThresholdEnabled = input<boolean>();

    onAddNewCheckpoint = output<number>();
    navigateToEditCheckpoint = output<{ checkpointIndex: number; sectionIndex: number }>();

    selectedLanguageId = signal<number>(0);

    public ngOnInit(): void {
        this.subscribeOnLanguageChange();
    }

    protected getNewCheckpointGroupInstance = (): ICheckpointGroup => {
        return this.fiChecklistHelperService.getCheckpointGroupInstanceV2();
    };

    private subscribeOnLanguageChange() {
        this.fiChecklistStoreService.selectedLanguageIdChange$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((languageId: number) => {
                this.selectedLanguageId.set(languageId);
            });
    }

    protected isSectionScoringDataShown(section: ICheckpointGroup): boolean {
        return (
            this.isComplianceThresholdEnabled() &&
            !!section.Checkpoints.find((checkpoint: ICheckpointV2) => checkpoint.Scoring)
        );
    }

    protected copyCheckpoint(checkpoint: ICheckpointV2, sectionIndex: number) {
        const checkpointCopy = this.fiCheckpoint2HelperService.getCheckpointCopyInstanceV2(checkpoint);
        this.checkpointGroups.update((groups) => {
            return groups.map((group, index) =>
                index === sectionIndex ? { ...group, Checkpoints: [...group.Checkpoints, checkpointCopy] } : group,
            );
        });
    }

    protected confirmCheckpointRemove(checkpoint: ICheckpointV2, sectionIndex: number): void {
        const hasCheckpointAnswersPromise = new Promise<TResultPromiseData>((resolve, reject) => {
            if (!checkpoint.Id) {
                // new checkpoints have no answers
                resolve({ result: false });
            } else {
                // check if checkpoint has answers via backend
                this.fiChecklist2Service.hasCheckpointAnswers(checkpoint.Id).then((hasCheckpointAnswers) => {
                    resolve({ result: hasCheckpointAnswers });
                }, reject);
            }
        });

        hasCheckpointAnswersPromise.then((hasCheckpointAnswersResult) => {
            const confirmDeletePromise = new Promise<TResultPromiseData>((resolve, reject) => {
                if (!hasCheckpointAnswersResult.result) {
                    this.fiDialogsService
                        .confirmSimpleDelete({
                            headerText: this.appTextService.get('checklist.checkpoint_delete'),
                            messageText: this.appTextService.get('checklist.checkpoint_delete_confirm_message'),
                            preventUppercaseButtonLabel: true,
                            config: {
                                panelClass: ['fi-rounded-dialog-panel', 'fi-confirm-delete-dialog-panel'],
                                autoFocus: false,
                            },
                        })
                        .onYesButtonClick(() => resolve({ result: true }))
                        .onNoButtonClick(() => resolve({ result: false }));
                } else {
                    this.fiDialogsService
                        .custom({
                            headerText: this.appTextService.get('checklist.checkpoint_delete'),
                            headerIconClass: 'fa-regular fa-circle-exclamation red',
                            colorClass: 'red',
                            isTopCloseButtonShown: true,
                            contentComponentRef: FiDialogConfirmDeleteComponent,
                            preventUppercaseButtonLabel: true,
                            contentComponentData: {
                                step1: {
                                    message: 'checklist.checkpoint_delete_dialog_confirm_text',
                                    buttonText: this.appTextService.get('common.confirm'),
                                    confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                                    preventUppercaseButtonLabel: true,
                                },
                            },
                            config: {
                                maxWidth: '600px',
                                panelClass: ['fi-rounded-dialog-panel'],
                            },
                        })
                        .onYesButtonClick(() => resolve({ result: true }))
                        .onNoButtonClick(() => resolve({ result: false }));
                }
            });

            confirmDeletePromise.then((confirmDeleteResult) => {
                if (confirmDeleteResult.result) {
                    this.removeCheckpoint(checkpoint, sectionIndex);
                }
            });
        });
    }

    private removeCheckpoint(checkpoint: ICheckpointV2, sectionIndex: number) {
        this.checkpointGroups.update((groups) => {
            return groups.map((group, index) =>
                index === sectionIndex
                    ? { ...group, Checkpoints: group.Checkpoints.filter((c) => !isEqual(c, checkpoint)) }
                    : group,
            );
        });
    }

    private ngOnDestroy$: Subject<void> = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly ObjectTypes = ObjectTypes;
}
